import type { API, InlineTool } from "@editorjs/editorjs";

/**
 * WordPress-style inline link tool with SEO controls.
 *
 * Replaces the default Editor.js inline link tool (registered under the
 * "link" key). Shows a popover with a URL field plus two checkboxes:
 * "Open in new tab" and "Mark as nofollow".
 *
 * Every link this tool saves carries an explicit `target` and `rel` so the
 * website renderer can distinguish an editor's deliberate choice from legacy
 * links that never had one:
 *   - new tab      → target="_blank" rel="noopener noreferrer ..."
 *   - same tab     → target="_self"
 *   - nofollow on  → rel includes "nofollow"
 *   - nofollow off → rel includes "follow" (marker token; the website
 *                    renderer strips it and treats the link as follow)
 */
export default class SmartLink implements InlineTool {
  static get isInline() {
    return true;
  }

  static get title() {
    return "Link";
  }

  static get shortcut() {
    return "CMD+K";
  }

  static get sanitize() {
    return {
      a: {
        href: true,
        target: true,
        rel: true,
      },
    };
  }

  private api: API;
  private button: HTMLButtonElement;
  private actions: HTMLDivElement;
  private urlInput: HTMLInputElement;
  private newTabCheckbox: HTMLInputElement;
  private nofollowCheckbox: HTMLInputElement;
  private removeButton: HTMLButtonElement;

  private savedRange: Range | null = null;
  private currentAnchor: HTMLAnchorElement | null = null;

  constructor({ api }: { api: API }) {
    this.api = api;

    this.button = document.createElement("button");
    this.button.type = "button";
    this.button.classList.add(this.api.styles.inlineToolButton);
    this.button.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>`;

    this.actions = document.createElement("div");
    this.actions.classList.add("smartlink-popover");
    this.actions.hidden = true;

    this.urlInput = document.createElement("input");
    this.urlInput.type = "text";
    this.urlInput.placeholder = "Paste or type a link…";
    this.urlInput.classList.add("smartlink-popover__input");
    this.urlInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        this.applyLink();
      }
    });

    this.newTabCheckbox = document.createElement("input");
    this.nofollowCheckbox = document.createElement("input");

    const newTabLabel = this.buildCheckbox(this.newTabCheckbox, "Open in new tab");
    const nofollowLabel = this.buildCheckbox(this.nofollowCheckbox, "Mark as nofollow");

    const applyButton = document.createElement("button");
    applyButton.type = "button";
    applyButton.textContent = "Apply";
    applyButton.classList.add("smartlink-popover__btn", "smartlink-popover__btn--apply");
    applyButton.addEventListener("click", () => this.applyLink());

    this.removeButton = document.createElement("button");
    this.removeButton.type = "button";
    this.removeButton.textContent = "Remove";
    this.removeButton.classList.add("smartlink-popover__btn", "smartlink-popover__btn--remove");
    this.removeButton.addEventListener("click", () => this.removeLink());

    const buttonsRow = document.createElement("div");
    buttonsRow.classList.add("smartlink-popover__buttons");
    buttonsRow.append(applyButton, this.removeButton);

    this.actions.append(this.urlInput, newTabLabel, nofollowLabel, buttonsRow);
  }

  private buildCheckbox(input: HTMLInputElement, text: string): HTMLLabelElement {
    input.type = "checkbox";
    input.classList.add("smartlink-popover__checkbox");
    const label = document.createElement("label");
    label.classList.add("smartlink-popover__option");
    const span = document.createElement("span");
    span.textContent = text;
    label.append(input, span);
    return label;
  }

  render(): HTMLElement {
    return this.button;
  }

  renderActions(): HTMLElement {
    return this.actions;
  }

  surround(range: Range): void {
    if (!range) return;

    this.savedRange = range.cloneRange();
    this.currentAnchor = this.api.selection.findParentTag(
      "A"
    ) as HTMLAnchorElement | null;

    if (this.currentAnchor) {
      this.api.selection.expandToTag(this.currentAnchor);
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        this.savedRange = selection.getRangeAt(0).cloneRange();
      }
      this.urlInput.value = this.currentAnchor.getAttribute("href") || "";
      this.newTabCheckbox.checked =
        this.currentAnchor.getAttribute("target") !== "_self";
      this.nofollowCheckbox.checked = (
        this.currentAnchor.getAttribute("rel") || ""
      ).includes("nofollow");
    } else {
      this.urlInput.value = "";
      this.newTabCheckbox.checked = true;
      this.nofollowCheckbox.checked = false;
    }

    this.removeButton.hidden = !this.currentAnchor;
    this.actions.hidden = false;
    this.urlInput.focus();
  }

  checkState(): boolean {
    const anchor = this.api.selection.findParentTag("A");
    this.button.classList.toggle(
      this.api.styles.inlineToolButtonActive,
      Boolean(anchor)
    );
    return Boolean(anchor);
  }

  clear(): void {
    this.actions.hidden = true;
    this.savedRange = null;
    this.currentAnchor = null;
  }

  private normalizeUrl(raw: string): string {
    const url = raw.trim();
    if (!url) return "";
    if (/^(https?:|mailto:|tel:|#|\/|\?)/i.test(url)) return url;
    return `https://${url}`;
  }

  private applyLink(): void {
    const href = this.normalizeUrl(this.urlInput.value);
    if (!href) {
      this.urlInput.focus();
      return;
    }

    const newTab = this.newTabCheckbox.checked;
    const relParts: string[] = [];
    if (newTab) relParts.push("noopener", "noreferrer");
    relParts.push(this.nofollowCheckbox.checked ? "nofollow" : "follow");

    let anchor = this.currentAnchor;

    if (!anchor) {
      if (!this.savedRange || this.savedRange.collapsed) return;

      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(this.savedRange);

      anchor = document.createElement("a");
      anchor.appendChild(this.savedRange.extractContents());
      this.savedRange.insertNode(anchor);
    }

    anchor.href = href;
    anchor.target = newTab ? "_blank" : "_self";
    anchor.rel = relParts.join(" ");

    this.finish();
  }

  private removeLink(): void {
    const anchor = this.currentAnchor;
    if (!anchor) return;

    const parent = anchor.parentNode;
    if (parent) {
      while (anchor.firstChild) {
        parent.insertBefore(anchor.firstChild, anchor);
      }
      parent.removeChild(anchor);
      parent.normalize();
    }

    this.finish();
  }

  private finish(): void {
    this.clear();
    this.api.inlineToolbar.close();
  }
}
